import moment from "moment";

import type { Account, Position } from "~/v2/api/rest/account";
import type { OptionContract, Quote } from "~/v2/api/rest/data";
import type { CreateOrderWithIntent } from "~/v2/api/rest/order";
import type { PositionIntent } from "~/v2/constants/optionOrder";
import { isGTZero } from "../shared/utils";
import type { OptionSchema } from "./schema";

export const getExpirationLabel = (
  expDate: OptionContract["expiration_date"] | undefined,
  dateFormat: "MMM DD, YYYY" | "MM/DD/YYYY",
  dotOrBrackets: "dot" | "bracket" = "dot",
) => {
  if (!expDate) {
    return "";
  }

  // Truncate the time part from both dates to compare just the dates
  const today = moment().startOf("day");
  const expirationDate = moment(expDate).startOf("day");
  const daysUntilExpiration = expirationDate.diff(today, "days");
  const formattedDate = moment(expDate).format(dateFormat);

  const dayLabel = `${
    daysUntilExpiration === 0
      ? "Today"
      : `${daysUntilExpiration} ${daysUntilExpiration > 1 ? "days" : "day"}`
  }`;

  if (dotOrBrackets === "bracket") {
    return `${formattedDate} (${dayLabel})`;
  }

  return `${formattedDate} • ${dayLabel}`;
};

export const getDefaultPositionIntent = (
  quoteSide: string | undefined,
  symbol: string,
  positions: Position[] | undefined,
): PositionIntent => {
  const position = positions?.find((p) => p.symbol === symbol);
  const qty = position ? parseFloat(position.qty) : 0;

  if (quoteSide === "bid") {
    return qty > 0 ? "sell_to_close" : "sell_to_open";
  }
  return qty < 0 ? "buy_to_close" : "buy_to_open";
};

export const getSideFromIntent = (intent: PositionIntent): "buy" | "sell" =>
  intent.startsWith("buy") ? "buy" : "sell";

export const createOptionOrderWithIntentRequest = ({
  symbol,
  positionIntent,
  orderType,
  timeInForce,
  quantity,
  limitPrice,
  stopPrice,
}: OptionSchema & {
  symbol: string;
  positionIntent: PositionIntent;
}): CreateOrderWithIntent => {
  const request: CreateOrderWithIntent = {
    symbol,
    position_intent: positionIntent,
    type: orderType,
    time_in_force: timeInForce,
    qty: quantity.toString(),
  };

  if (
    (orderType === "limit" || orderType === "stop_limit") &&
    limitPrice !== undefined
  ) {
    request.limit_price = limitPrice.toString();
  }

  if (
    (orderType === "stop" || orderType === "stop_limit") &&
    stopPrice !== undefined
  ) {
    request.stop_price = stopPrice.toString();
  }

  return request;
};

export const getIsSubmitDisabled = ({
  account,
  contract,
  quote,
  product,
  quantity,
  orderType,
  limitPrice,
  stopPrice,
}: {
  account: Account | undefined;
  contract: OptionContract | undefined;
  quote: Quote | undefined;
  product: string | undefined;
  quantity: number;
  orderType: OptionSchema["orderType"];
  limitPrice: number | undefined;
  stopPrice: number | undefined;
}): boolean => {
  if (!account || !contract || !quote || !product) {
    return true;
  }
  if (!isGTZero(quantity)) {
    return true;
  }
  if (
    (orderType === "limit" || orderType === "stop_limit") &&
    !isGTZero(limitPrice)
  ) {
    return true;
  }
  if (
    (orderType === "stop" || orderType === "stop_limit") &&
    !isGTZero(stopPrice)
  ) {
    return true;
  }
  return false;
};
